import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Grid2,
  Typography,
} from "@mui/material"
import { CollapsibleCard } from "components/CollapsiblCard/CollapsibleCard"
import { DeviceList } from "components/DevicesList/DeviceList"
import { ExaminationInfo } from "components/ExaminationInfo/ExaminationInfo"
import { ProjectInfos } from "components/ProjectInfos/ProjectInfos"
import { StatusSelect } from "components/StatusSelect/StatusSelect"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { AppRoutes } from "store/routes"
import { emptyPerson } from "store/store"
import { allExaminations } from "utils/allExaminations"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"
import { Device, DeviceType, Examination, History, Status, User } from "../../graphql/types"
import { DocumentCard } from "./DocumentCard"

export const ExaminationPage = () => {
  const { projectID, examinationID } = useParams()
  const navigate = useNavigate()
  const [examination, setExamination] = useState<Examination | null>(null)

  const projectsState = useStoreState((state) => state.projects)
  const [user] = useLocalStorage<User>("user", emptyPerson)

  const fetchProjects = useStoreActions((actions) => actions.fetchProjects)
  const updateExamintation = useStoreActions((state) => state.updateExamination)

  const project = projectsState.find((p) => p && p.id === projectID)

  // Ensure projects are loaded when the page is hard-reloaded
  useEffect(() => {
    if (projectsState.length === 0) {
      // Fetch projects silently (no fresh flag needed – store is empty)
      fetchProjects({ fresh: false })
    }
  }, [projectsState.length, fetchProjects])

  useEffect(() => {
    const exam = project
      ? allExaminations(project).find((examination) => examination.id === examinationID)
      : undefined
    if (exam) {
      setExamination(exam)
    }
  }, [examinationID, project, projectID, projectsState])

  const getDevicesByType = (type: DeviceType): Device[] => {
    if (project) {
      let devices = project.devices.filter((item) => item.type === type)

      if (type === DeviceType.EUT_GROUP) {
        devices = devices.filter((item) =>
          (item as any).examinations?.some(
            (examination: { id: string }) => examination.id === examinationID
          )
        )
      }

      return devices
    }
    return []
  }

  const handleEutClick = (device: Device) => {
    // Navigate to Project Detail page, devices tab, with the selected device
    if (projectID) {
      navigate(`${AppRoutes.PROJECTS}/${projectID}`, {
        state: {
          activeTab: 1, // Devices tab is typically index 1
          selectedDeviceId: device.id,
        },
      })
    } else {
      // Fallback to EUT Management if no project context
      navigate(AppRoutes.EUTMANAGMENT, { state: { selectedEutId: device.id } })
    }
  }

  const handleChangeStatus = (status: Status) => {
    if (examination && projectID) {
      const names = new Set(examination.examinationConfig.tasks.map((item) => item.name))
      const tasksClosed = examination.tasks.filter(
        (item) => names.has(item.name) && item.status !== Status.DONE
      )

      if (status === Status.DONE && tasksClosed.length > 0) {
        const missingExams = tasksClosed.flatMap((item) => item.name).join(", ")
        enqueueSnackbar(
          `Die Prüfung kann nicht abgeschlossen werden. Folgende Arbeitsschritte wurden noch nicht abgeschlossen: ${missingExams}`,
          { variant: "warning" }
        )
      } else {
        // Create history entry for status change
        const statusHistory: History = {
          id: v4(),
          createdAt: new Date().toISOString(),
          createdBy: user,
          description: [`Status geändert von ${examination.status} zu ${status}`],
        }

        // Set timestamps based on status changes
        const updatedExamination = {
          ...examination,
          status: status,
          history: [...(examination.history || []), statusHistory],
        }

        // Set startTimestamp when moving from OPEN to IN_PROGRESS (when work actually begins)
        if (examination.status === Status.OPEN && status === Status.IN_PROGRESS) {
          updatedExamination.startTimestamp = new Date().toISOString()
        }

        // Clear endTimestamp when reopening from DONE to IN_PROGRESS (retesting)
        if (examination.status === Status.DONE && status === Status.IN_PROGRESS) {
          updatedExamination.endTimestamp = null
          // If there's no startTimestamp yet (shouldn't happen, but just in case), set it
          if (!examination.startTimestamp) {
            updatedExamination.startTimestamp = new Date().toISOString()
          }
        }

        // Clear endTimestamp when reopening from DONE to OPEN (preparing for retest)
        if (examination.status === Status.DONE && status === Status.OPEN) {
          updatedExamination.endTimestamp = null
        }

        // Set endTimestamp when moving to DONE
        if (status === Status.DONE) {
          updatedExamination.endTimestamp = new Date().toISOString()
        }

        updateExamintation(updatedExamination)
        enqueueSnackbar(`Status erfolgreich geändert`, { variant: "success" })
      }
    }
  }

  // Loading state
  if (!project || !examination) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      {examination && (
        <Grid2 container spacing={1}>
          <Grid2 size={12}>
            <ProjectInfos examination={examination} />
          </Grid2>

          {/* Examination Details with Operating Modes */}
          <Grid2 size={12}>
            <ExaminationInfo examination={examination} showOperatingModes={true} />
          </Grid2>

          {/* Prüflinge */}
          <Grid2 size={12}>
            <CollapsibleCard open={true} title={"Devices"}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {getDevicesByType(DeviceType.EUT_GROUP).length > 0 && (
                  <DeviceList
                    devices={getDevicesByType(DeviceType.EUT_GROUP)}
                    onDeviceClick={handleEutClick}
                  />
                )}
                {getDevicesByType(DeviceType.AUX).length > 0 && (
                  <DeviceList devices={getDevicesByType(DeviceType.AUX)} />
                )}
                {getDevicesByType(DeviceType.CONSUMABLE).length > 0 && (
                  <DeviceList devices={getDevicesByType(DeviceType.CONSUMABLE)} />
                )}
                {getDevicesByType(DeviceType.TEST_EQUIPMENT).length > 0 && (
                  <DeviceList devices={getDevicesByType(DeviceType.TEST_EQUIPMENT)} />
                )}
              </Box>
            </CollapsibleCard>
          </Grid2>

          {/* Special Arrangements */}
          {examination.specialArrangements.length > 0 && (
            <Grid2 size={12}>
              <CollapsibleCard open={true} title="Sonderabsprachen">
                <Typography variant="body2">{examination.specialArrangements}</Typography>
              </CollapsibleCard>
            </Grid2>
          )}

          {/* Deviations */}
          {examination.deviations && examination.deviations.length > 0 && (
            <Grid2 size={12}>
              <CollapsibleCard open={true} title="Abweichungen">
                <Typography variant="body2">{examination.deviations}</Typography>
              </CollapsibleCard>
            </Grid2>
          )}

          {/* Documents */}
          {examinationID && (
            <Grid2 size={12}>
              <DocumentCard examination={examination} project={project} showDeviceImages={true} />
            </Grid2>
          )}
          <Grid2 size={12}>
            <Card>
              <CardHeader
                title="Status ändern"
                slotProps={{ title: { variant: "h3" } }}
                sx={{ padding: "1rem 1rem 0.5rem 1rem" }}
              />

              <CardContent>
                <StatusSelect
                  value={examination.status}
                  onChange={(status) => handleChangeStatus(status)}
                />
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      )}
    </Container>
  )
}
